import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CountdownTimer } from '../CountdownTimer';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Clock, Package, CheckCircle, MapPin, XCircle, AlertCircle, Loader2, RotateCcw, User, Store, Monitor, Utensils } from 'lucide-react';
import type { Restaurant } from '@/types';
import { storeApi } from '@/lib/api';
import type { Menu, Order, OrderItemDetail, OrderItemSelectedOption, OrderStatus } from '@/lib/api/types';
import { TERMINAL_ORDER_STATUSES } from '@/lib/api/types';
import { useLanguage } from '@/contexts/AppContext';
import { DEFAULT_STORE_TIMEZONE } from '@/utils/menuAvailability';
import type { TranslationStrings } from '@/i18n/translations';
import type { Locale } from '@/config/locales';
import { logger } from '@/utils/logger';
import { activeOrderDisplayNumber, activeOrderSecondaryNumber } from '@/utils/orderDisplay';

interface OrdersContentProps {
  orders: Order[];
  isLoading?: boolean;
  error?: Error | null;
  /**
   * #659: 인가 거절(401/403)로 표시물 0건일 때 true — 일반 빈 상태와 구분된
   * '인증 필요' 안내를 표시한다 (silent empty 금지, useActiveOrders.authRequired).
   */
  authRequired?: boolean;
  expandedOrders: string[];
  onToggleExpand: (orderId: string) => void;
  restaurants: Restaurant[];
  /** 주문 취소 핸들러 (없으면 취소 버튼 비표시) */
  onCancelOrder?: (orderId: number) => void;
}

type OrderMenuLocaleIndex = {
  menuNames: Record<number, string>;
  optionGroupNames: Record<number, string>;
  optionNames: Record<number, string>;
};

/**
 * ISO datetime에서 남은 시간(분) 계산
 */
function calculateRemainingMinutes(estimatedReadyTime: string | null | undefined): number {
  if (!estimatedReadyTime) return 0;
  const readyTime = new Date(estimatedReadyTime).getTime();
  const now = Date.now();
  const diffMs = readyTime - now;
  return Math.max(0, Math.ceil(diffMs / 60000));
}

/**
 * 픽업 위치 표시 텍스트 결정
 * 우선순위: pickup_location > store.name > store_name > 번역 > 기본값
 */
function getPickupDisplayText(
  order: Order,
  fallbackText: string
): string {
  return order.pickup_location
    || order.store?.name
    || order.store_name
    || fallbackText;
}

function getOrderTypeDisplayLabel(
  order: Order,
  translations: TranslationStrings['orders']
): string | undefined {
  if (!order.order_type_label) return undefined;

  return order.order_type === 'table'
    ? translations.orderTypeTable
    : translations.orderTypePickup;
}

function getOrderMenuLocaleCacheKey(locale: Locale, storeId: number): string {
  return `${locale}:${storeId}`;
}

function buildOrderMenuLocaleIndex(menus: Menu[]): OrderMenuLocaleIndex {
  const menuNames: Record<number, string> = {};
  const optionGroupNames: Record<number, string> = {};
  const optionNames: Record<number, string> = {};

  for (const menu of menus) {
    menuNames[menu.id] = menu.name;

    for (const optionGroup of menu.options ?? []) {
      optionGroupNames[optionGroup.id] = optionGroup.name;

      for (const option of optionGroup.options ?? []) {
        optionNames[option.id] = option.name;
      }
    }
  }

  return { menuNames, optionGroupNames, optionNames };
}

function getLocalizedOrderMenuName(
  item: OrderItemDetail,
  localeIndex: OrderMenuLocaleIndex | undefined
): string {
  return localeIndex?.menuNames[item.menu_id] || item.menu_name;
}

function getLocalizedSelectedOptionName(
  option: OrderItemSelectedOption,
  localeIndex: OrderMenuLocaleIndex | undefined
): string {
  if (!option.is_selection_type) {
    return option.display_value || option.input_value || '';
  }

  if (option.menu_option_id != null) {
    return localeIndex?.optionNames[option.menu_option_id]
      || option.option_name
      || option.display_value
      || '';
  }

  const groupName = localeIndex?.optionGroupNames[option.menu_option_group_id] || option.group_name || '';
  if (option.option_name) {
    return groupName ? `${groupName}: ${option.option_name}` : option.option_name;
  }

  return option.display_value || '';
}

function getLocalizedOrderOptionNames(
  item: OrderItemDetail,
  localeIndex: OrderMenuLocaleIndex | undefined
): string[] {
  const snapshotOptionNames = item.options?.map(o => o.option_name).filter(Boolean) || [];
  if (!localeIndex && snapshotOptionNames.length > 0) {
    return snapshotOptionNames;
  }

  const selectedOptionNames = item.selected_options
    ?.map((option) => getLocalizedSelectedOptionName(option, localeIndex))
    .filter((optionName) => optionName.length > 0);

  if (selectedOptionNames && selectedOptionNames.length > 0) {
    return selectedOptionNames;
  }

  return snapshotOptionNames;
}

/**
 * 상태 변경 이력의 아이콘 결정
 */
function getTimelineIcon(status: OrderStatus): React.ReactNode {
  switch (status) {
    case 'queued':
    case 'pending':
      return <Loader2 className="h-3 w-3" />;
    case 'confirmed':
      return <CheckCircle className="h-3 w-3" />;
    case 'preparing':
      return <Clock className="h-3 w-3" />;
    case 'ready':
      return <Package className="h-3 w-3" />;
    case 'completed':
      return <CheckCircle className="h-3 w-3" />;
    case 'cancelled':
      return <XCircle className="h-3 w-3" />;
    case 'failed':
      return <AlertCircle className="h-3 w-3" />;
    default:
      return <Clock className="h-3 w-3" />;
  }
}

/**
 * 환불 상태 배지 색상
 */
function getRefundBadgeColor(status: string): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-500 text-white';
    case 'partial':
      return 'bg-orange-500 text-white';
    case 'full':
      return 'bg-green-600 text-white';
    default:
      return 'bg-muted-foreground text-white';
  }
}

export function OrdersContent({
  orders,
  isLoading = false,
  error = null,
  authRequired = false,
  expandedOrders,
  onToggleExpand,
  restaurants,
  onCancelOrder,
}: OrdersContentProps) {
  const { locale, t } = useLanguage();
  const [menuLocaleIndexes, setMenuLocaleIndexes] = useState<Record<string, OrderMenuLocaleIndex>>({});
  const pendingMenuIndexKeysRef = useRef<Set<string>>(new Set());
  const hasOrders = orders.length > 0;

  useEffect(() => {
    const expandedStoreIds = Array.from(new Set(
      orders
        .filter((order) => expandedOrders.includes(String(order.id)) && (order.items?.length ?? 0) > 0)
        .map((order) => order.store_id)
    ));

    for (const storeId of expandedStoreIds) {
      const cacheKey = getOrderMenuLocaleCacheKey(locale, storeId);
      if (menuLocaleIndexes[cacheKey] || pendingMenuIndexKeysRef.current.has(cacheKey)) {
        continue;
      }

      pendingMenuIndexKeysRef.current.add(cacheKey);
      storeApi.getMenus(String(storeId), { filter_available: false })
        .then((response) => {
          setMenuLocaleIndexes((current) => ({
            ...current,
            [cacheKey]: buildOrderMenuLocaleIndex(response.data.menus),
          }));
        })
        .catch((error) => {
          logger.warn('[OrdersContent] Failed to load localized menu names for order detail:', {
            locale,
            storeId,
            error,
          });
        })
        .finally(() => {
          pendingMenuIndexKeysRef.current.delete(cacheKey);
        });
    }
  }, [orders, expandedOrders, locale, menuLocaleIndexes]);

  // 주문 상태별 UI 정보 (useCallback으로 메모이제이션)
  const getStatusInfo = useCallback((status: OrderStatus) => {
    switch (status) {
      case 'queued':
      case 'pending':
        return {
          icon: <Loader2 className="h-5 w-5 animate-spin" />,
          label: t.orders.pending || '대기 중',
          badgeColor: 'bg-muted-foreground text-white',
        };
      case 'confirmed':
        return {
          icon: <CheckCircle className="h-5 w-5" />,
          label: t.orders.confirmed || '확인됨',
          badgeColor: 'bg-primary text-white',
        };
      case 'preparing':
        return {
          icon: <Clock className="h-5 w-5" />,
          label: t.orders.preparing || '준비 중',
          badgeColor: 'bg-destructive text-white',
        };
      case 'ready':
        return {
          icon: <Package className="h-5 w-5" />,
          label: t.orders.ready || '픽업 준비 완료',
          badgeColor: 'bg-secondary text-white',
        };
      case 'completed':
        return {
          icon: <CheckCircle className="h-5 w-5" />,
          label: t.orders.completed || '완료',
          badgeColor: 'bg-primary text-white',
        };
      case 'cancelled':
        return {
          icon: <XCircle className="h-5 w-5" />,
          label: t.orders.cancelled || '취소됨',
          badgeColor: 'bg-red-600 text-white',
        };
      case 'failed':
        return {
          icon: <AlertCircle className="h-5 w-5" />,
          label: t.orders.failed || '실패',
          badgeColor: 'bg-red-600 text-white',
        };
      default:
        return {
          icon: <Clock className="h-5 w-5" />,
          label: status,
          badgeColor: 'bg-muted-foreground text-white',
        };
    }
  }, [t]);

  return (
    <div className="flex-1 pb-[calc(5rem+2vw)]">
      <div className="p-[min(5vw,1.25rem)]">
        <h2 className="text-[calc(1.25rem+0.5vw)] font-medium text-foreground mb-[min(4vw,1.5rem)]">
          {t.orders.activeOrdersTitle ?? t.orders.pickupOrdersTitle}
        </h2>

        <div className="space-y-[min(4vw,1.25rem)]">
          {isLoading && !hasOrders ? (
            <div
              role="status"
              aria-live="polite"
              data-testid="active-orders-loading"
              className="flex flex-col items-center justify-center py-16 text-center"
            >
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" aria-hidden="true" />
              <p className="text-[clamp(14px,3.5vw,16px)] text-muted-foreground">
                {t.orders.loadingActiveOrders}
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div
                  role="alert"
                  data-testid="active-orders-error"
                  className="flex items-start gap-3 rounded-[5px] border border-destructive/30 bg-destructive/10 p-4 text-left"
                >
                  <AlertCircle className="h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
                  <div className="min-w-0">
                    <h3 className="text-[clamp(14px,3.5vw,16px)] font-medium text-foreground">
                      {t.orders.activeOrdersLoadError}
                    </h3>
                    <p className="mt-1 text-[clamp(12px,3vw,14px)] text-muted-foreground break-words">
                      {error.message || t.orders.activeOrdersLoadErrorMessage}
                    </p>
                  </div>
                </div>
              )}

              {/* #659: 인가 거절(401/403) 빈 상태 — 일반 빈 상태와 구분된 인증 안내 (silent empty 금지) */}
              {!error && !hasOrders && authRequired && (
            <div
              role="status"
              data-testid="active-orders-auth-required"
              className="flex flex-col items-center justify-center py-16 text-center"
            >
              <User className="h-16 w-16 text-border mb-4" aria-hidden="true" />
              <p className="text-[clamp(14px,3.5vw,16px)] text-muted-foreground">
                {t.orders.authRequiredNotice}
              </p>
              <Link
                to="/order-history"
                className="mt-3 text-[clamp(12px,3vw,14px)] text-primary underline-offset-2 hover:underline"
              >
                {t.orders.viewOrderHistory ?? 'View order history'}
              </Link>
            </div>
              )}

              {!error && !hasOrders && !authRequired && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Package className="h-16 w-16 text-border mb-4" />
              <p className="text-[clamp(14px,3.5vw,16px)] text-muted-foreground">
                {t.orders.noActiveOrders ?? t.orders.noOrders}
              </p>
              <Link
                to="/order-history"
                className="mt-3 text-[clamp(12px,3vw,14px)] text-primary underline-offset-2 hover:underline"
              >
                {t.orders.viewOrderHistory ?? 'View order history'}
              </Link>
            </div>
              )}

              {orders.map((order) => {
            const orderId = String(order.id);
            const statusInfo = getStatusInfo(order.status);
            const isExpanded = expandedOrders.includes(orderId);
            const isTerminalStatus = (TERMINAL_ORDER_STATUSES as readonly string[]).includes(order.status);
            // #1919 P4: 진행중 주문만 짧은 순번(#N — 매장 POS 와 동일 번호, 구두 대조).
            // 완결 주문은 긴 번호 유지(일일 리셋 — 과거 내역 #1 중복 방지, 표시 범위 분리 정책).
            const orderNumberDisplay = isTerminalStatus
              ? `#${order.order_number || orderId}`
              : activeOrderDisplayNumber(order);
            const orderNumberSecondary = isTerminalStatus ? null : activeOrderSecondaryNumber(order);
            const estimatedMinutes = calculateRemainingMinutes(order.estimated_ready_time);
            // 픽업 위치 표시 텍스트 (함수 추출로 가독성 향상)
            const pickupDisplay = getPickupDisplayText(order, t.orders.pickupLocation || '픽업 위치');
            const orderTypeLabel = getOrderTypeDisplayLabel(order, t.orders);
            const menuLocaleIndex = menuLocaleIndexes[getOrderMenuLocaleCacheKey(locale, order.store_id)];

            return (
              <Card
                key={order.id}
                className="overflow-hidden bg-background rounded-[5px] border border-border mt-[0px] mr-[0px] mb-[10px] ml-[0px]"
              >
                {/* Clickable Header Section */}
                <button
                  onClick={() => onToggleExpand(orderId)}
                  aria-expanded={isExpanded}
                  aria-controls={`order-details-${orderId}`}
                  className="w-full p-[clamp(12px,3vw,16px)] text-left hover:bg-muted/50 transition-colors"
                >
                  <div className="space-y-[clamp(10px,2.5vw,14px)]">
                    {/* Header - Compact */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0 space-y-2">
                        {/* Store Name - First Line */}
                        <h3 className="text-[clamp(14px,3.5vw,16px)] font-bold text-foreground truncate">
                          {order.store_name}
                        </h3>

                        {/* Order Number + Type + Status Badge - Second Line */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[clamp(11px,2.8vw,13px)] text-muted-foreground">
                            {orderNumberDisplay}
                            {orderNumberSecondary && (
                              <span className="ml-1 opacity-60">{orderNumberSecondary}</span>
                            )}
                          </span>
                          {orderTypeLabel && (
                            <span className="text-[clamp(10px,2.5vw,12px)] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-[3px]">
                              {orderTypeLabel}
                            </span>
                          )}
                          <Badge
                            className={`${order.status_color ? 'text-white' : statusInfo.badgeColor} rounded-[5px] px-2 py-0.5 flex items-center gap-1 shrink-0 border-0`}
                            style={order.status_color ? { backgroundColor: order.status_color } : undefined}
                          >
                            {statusInfo.icon}
                            <span className="text-[clamp(10px,2.5vw,12px)] font-bold">
                              {statusInfo.label}
                            </span>
                          </Badge>
                        </div>
                      </div>

                      {/* Expand/Collapse Icon */}
                      <svg
                        className={`h-5 w-5 text-muted-foreground transition-transform duration-300 shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>

                    {/* Simplified Info Row */}
                    <div className="flex items-center gap-4 rounded-[5px] bg-muted py-[10px] px-[12px]">
                      {/* Time */}
                      {/* P1 시정 (code-reviewer): CountdownTimer 는 preparing/ready 일 때만 렌더.
                          queued/pending/confirmed 상태는 estimated_ready_time 이 신뢰 어렵고
                          타이머 prop 타입(`'preparing'|'ready'|'completed'`)과 불일치 → 강제
                          캐스팅 회귀 차단. 비-타이머 상태는 status_label 로 대체 표시. */}
                      {!isTerminalStatus && (order.status === 'preparing' || order.status === 'ready') && (
                        <div className="flex items-center gap-2 flex-1">
                          <Clock className="h-4 w-4 text-primary shrink-0" />
                          <div className="min-w-0">
                            <p className="text-[clamp(10px,2.5vw,12px)] text-muted-foreground">
                              {order.status === 'ready' ? t.orders.status : t.orders.estimatedTime}
                            </p>
                            <CountdownTimer
                              initialMinutes={estimatedMinutes}
                              status={order.status}
                            />
                          </div>
                        </div>
                      )}

                      {/* Items Count — BE 응답에서 items 누락 가능성 (guestLookup 분기 등) 방어 */}
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-secondary shrink-0" />
                        <span className="text-[clamp(12px,3vw,14px)] font-bold text-foreground">
                          {order.items?.length ?? 0}
                        </span>
                      </div>
                    </div>

                    {/* 위치/테이블 표시 — 픽업: 지도 버튼, 테이블: 테이블 번호 표시 */}
                    {order.order_type === 'table' ? (
                      <div
                        className="flex items-center gap-2 rounded-[5px] bg-muted px-[8px] py-[5px] w-full"
                        aria-label={`${t.orders.tableLabel ?? 'Table'} ${order.table_number ?? ''}`.trim()}
                      >
                        <Utensils className="h-4 w-4 text-secondary shrink-0" aria-hidden="true" />
                        <span className="text-[clamp(11px,2.8vw,13px)] text-foreground truncate">
                          {`${t.orders.tableLabel ?? 'Table'} ${order.table_number ?? ''}`.trim()}
                        </span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const restaurant = restaurants.find(r => r.id === String(order.store_id));
                          if (restaurant) {
                            if (restaurant.address) {
                              window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.address)}`, '_blank');
                            } else if (restaurant.lat && restaurant.lng) {
                              window.open(`https://www.google.com/maps/search/?api=1&query=${restaurant.lat},${restaurant.lng}`, '_blank');
                            } else {
                              const restaurantName = restaurant._name || t.restaurants[restaurant.nameKey]?.name || restaurant.nameKey;
                              window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurantName)}`, '_blank');
                            }
                          } else {
                            window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.store_name)}`, '_blank');
                          }
                        }}
                        aria-label={`${pickupDisplay} - ${t.orders.openMap}`}
                        className="flex items-center gap-2 rounded-[5px] bg-muted hover:bg-border transition-colors cursor-pointer px-[8px] py-[5px] text-left w-full"
                      >
                        <MapPin className="h-4 w-4 text-secondary shrink-0" aria-hidden="true" />
                        <span
                          className="text-[clamp(11px,2.8vw,13px)] text-foreground truncate"
                          title={pickupDisplay}
                        >
                          {pickupDisplay}
                        </span>
                      </button>
                    )}
                  </div>
                </button>

                {/* Expandable Items List */}
                <div
                  id={`order-details-${orderId}`}
                  className={`transition-all duration-300 ease-in-out ${
                    isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                  } overflow-hidden`}
                >
                  <div className="px-[clamp(12px,3vw,16px)] pb-[clamp(12px,3vw,16px)] pt-0">
                    <div className="border-t border-border pt-[clamp(10px,2.5vw,14px)]">

                      {/* 환불 상태 배지 */}
                      {order.refund_status && order.refund_status !== 'none' && (
                        <div className="mb-3">
                          <Badge className={`${getRefundBadgeColor(order.refund_status)} rounded-[5px] px-2 py-0.5 border-0`}>
                            <RotateCcw className="h-3 w-3 mr-1" />
                            <span className="text-[clamp(10px,2.5vw,12px)] font-bold">
                              {order.refund_status_label || (
                                order.refund_status === 'pending' ? t.orders.refundPending :
                                order.refund_status === 'partial' ? t.orders.refundPartial :
                                order.refund_status === 'full' ? t.orders.refundFull : ''
                              )}
                            </span>
                          </Badge>
                        </div>
                      )}

                      {/* 취소 정보 (취소된 주문만) */}
                      {order.status === 'cancelled' && (order.cancelled_by_label || order.cancellation_reason) && (
                        <div className="mb-3 p-3 rounded-[5px] bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900">
                          {order.cancelled_by_label && (
                            <p className="text-[clamp(11px,2.8vw,13px)] text-red-700 dark:text-red-400">
                              <span className="font-bold">
                                {order.cancelled_by === 'customer' ? <User className="h-3 w-3 inline mr-1" /> :
                                 order.cancelled_by === 'store' ? <Store className="h-3 w-3 inline mr-1" /> :
                                 <Monitor className="h-3 w-3 inline mr-1" />}
                                {order.cancelled_by_label}
                              </span>
                            </p>
                          )}
                          {order.cancellation_reason && (
                            <p className="text-[clamp(10px,2.5vw,12px)] text-red-600 dark:text-red-500 mt-1">
                              {order.cancellation_reason}
                            </p>
                          )}
                        </div>
                      )}

                      {/* 주문 항목 */}
                      <h4 className="text-[clamp(12px,3vw,14px)] font-bold text-foreground mb-3">
                        {t.orders.items}
                      </h4>
                      <div className="space-y-3">
                        {(order.items ?? []).map((item, itemIndex) => {
                          const itemName = getLocalizedOrderMenuName(item, menuLocaleIndex);
                          const optionNames = getLocalizedOrderOptionNames(item, menuLocaleIndex);

                          return (
                            <div
                              key={item.id || itemIndex}
                              className="p-3 rounded-[5px] bg-muted"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <span className="text-[clamp(12px,3vw,14px)] font-bold text-foreground">
                                  {itemName}
                                </span>
                                <span className="text-[clamp(11px,2.8vw,13px)] font-medium text-muted-foreground">
                                  x{item.quantity}
                                </span>
                              </div>
                              {optionNames.length > 0 && (
                                <div className="space-y-1 mt-2 pl-2 border-l-2 border-primary">
                                  {optionNames.map((optionName, optionIndex) => (
                                    <p
                                      key={optionIndex}
                                      className="text-[clamp(10px,2.5vw,12px)] text-muted-foreground"
                                    >
                                      • {optionName}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* 주문 타임라인 */}
                      {order.status_history && order.status_history.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-[clamp(12px,3vw,14px)] font-bold text-foreground mb-3">
                            {t.orders.timeline}
                          </h4>
                          <div className="relative pl-4">
                            {/* 세로 연결선 */}
                            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
                            <div className="space-y-3">
                              {order.status_history.map((entry) => (
                                <div key={entry.id} className="relative flex items-start gap-3">
                                  {/* 타임라인 도트 */}
                                  <div className={`relative z-10 flex items-center justify-center w-4 h-4 rounded-full shrink-0 -ml-4 ${
                                    entry.to_status === order.status
                                      ? 'bg-primary text-white'
                                      : 'bg-muted text-muted-foreground'
                                  }`}>
                                    {getTimelineIcon(entry.to_status)}
                                  </div>
                                  {/* 타임라인 내용 */}
                                  <div className="flex-1 min-w-0 -mt-0.5">
                                    <p className="text-[clamp(11px,2.8vw,13px)] font-bold text-foreground">
                                      {getStatusInfo(entry.to_status).label}
                                    </p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-[clamp(9px,2.2vw,11px)] text-muted-foreground">
                                        {new Date(entry.changed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: DEFAULT_STORE_TIMEZONE })}
                                      </span>
                                      {entry.changed_by && (
                                        <span className="text-[clamp(9px,2.2vw,11px)] text-muted-foreground">
                                          · {entry.changed_by}
                                        </span>
                                      )}
                                    </div>
                                    {entry.reason && (
                                      <p className="text-[clamp(9px,2.2vw,11px)] text-muted-foreground mt-0.5 italic">
                                        {entry.reason}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 주문 취소 버튼 (조건부 표시) */}
                      {order.is_customer_cancellable && onCancelOrder && (
                        <div className="mt-4 pt-3 border-t border-border">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(t.orders.cancelConfirm)) {
                                onCancelOrder(order.id);
                              }
                            }}
                            className="w-full py-2.5 px-4 rounded-[5px] bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 text-[clamp(12px,3vw,14px)] font-bold hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors border border-red-200 dark:border-red-800"
                          >
                            <XCircle className="h-4 w-4 inline mr-2" />
                            {t.orders.cancelOrder}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
